import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ReactNode, useState, useEffect, useCallback } from 'react';

interface AppLayoutProps {
  children: ReactNode;
}

function AppLayout({ children }: AppLayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `px-4 py-2 rounded-xl text-sm font-medium tracking-wide transition-all duration-150 ${
      isActive
        ? 'bg-espresso-700/80 text-cream-100 border border-gold-600/30 shadow-soft'
        : 'text-espresso-200 hover:bg-espresso-800/60 hover:text-cream-50 border border-transparent'
    }`;

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ backgroundColor: '#f4ebda' }} dir="rtl">
      {!isFullscreen && (
        <nav className="shadow-strong border-b border-espresso-950/20"
             style={{ background: 'linear-gradient(to left, #231308, #3d2411)' }}>
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-5">
                <img
                  src="/icon.png"
                  alt="דאדא"
                  className="h-10 w-auto rounded-xl border border-gold-600/30 shadow-medium"
                />
                {/* Brand name */}
                <span className="text-cream-100 text-lg font-semibold tracking-widest uppercase"
                      style={{ fontFamily: "'Frank Ruhl Libre', serif", letterSpacing: '0.2em' }}>
                  דאדא
                </span>
                <div className="w-px h-6 bg-espresso-700/60 mx-1" />
                <div className="flex gap-1">
                  <NavLink to="/dashboard" className={navLinkClass}>לוח בקרה</NavLink>
                  <NavLink to="/manager" className={navLinkClass}>מנהל</NavLink>
                  <NavLink to="/operator/1" className={navLinkClass}>תנור 1</NavLink>
                  <NavLink to="/operator/2" className={navLinkClass}>תנור 2</NavLink>
                  <NavLink to="/vision" className={navLinkClass}>ראייה</NavLink>
                  <NavLink to="/sensors" className={navLinkClass}>חיישנים</NavLink>
                  <NavLink to="/logs" className={navLinkClass}>לוגים</NavLink>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-espresso-300 text-xs font-light tracking-widest uppercase">
                  {user?.name}
                </span>
                <button
                  onClick={handleLogout}
                  className="px-3.5 py-1.5 bg-transparent hover:bg-espresso-800/60 text-espresso-300 hover:text-cream-100 border border-espresso-700/40 rounded-xl text-xs font-medium tracking-wide transition-all duration-150 active:scale-95"
                >
                  התנתק
                </button>
              </div>
            </div>
          </div>
        </nav>
      )}
      <main className={`flex-1 min-h-0 overflow-y-auto w-full px-4 sm:px-6 lg:px-8 ${isFullscreen ? 'py-2' : 'py-6'}`}>
        {children}
      </main>
      <button
        onClick={toggleFullscreen}
        title={isFullscreen ? 'יציאה ממסך מלא' : 'מסך מלא'}
        className="fixed bottom-5 left-5 z-50 backdrop-blur-sm text-espresso-300 hover:text-cream-100 rounded-xl w-10 h-10 flex items-center justify-center shadow-strong transition-all duration-150 active:scale-95 border border-espresso-700/40 text-sm"
        style={{ background: 'rgba(35,19,8,0.85)' }}
      >
        {isFullscreen ? '⊡' : '⊞'}
      </button>
    </div>
  );
}

export default AppLayout;
